// Falcon Inventory (Phase 1, approved 01/11/2026), shared by the server and the screens.
// An optional module: off for every organisation until an Admin switches it on (Settings → Inventory). Falcon keeps
// the stock count for every location. Stock moves only through numbered documents:
//   GRN  Goods Receive Note: a shipment (or goods without a shipment) received into a location;
//   ADJ  Adjustment: a manual change with a reason code (large ones can wait for an Admin's approval);
//   TRF  Transfer: stock moved from one location to another;
//   OPN  Opening stock: the stock held on the day inventory starts.
// Stock is kept per item (garment, fabric or trim) × colour × size × location; a garment's SKU is shown with it.

export const DOC_KINDS = ["GRN", "ADJ", "TRF", "OPN"] as const;
export type DocKind = (typeof DOC_KINDS)[number];
export const DOC_KIND_LABEL: Record<DocKind, string> = { GRN: "Goods received", ADJ: "Adjustment", TRF: "Transfer", OPN: "Opening stock" };
export const DOC_STATUS_LABEL: Record<string, string> = { posted: "Posted", pending: "Waiting for approval", rejected: "Not approved", undone: "Undone" };

/** GRN-00018, ADJ-00001… */
export const docNumber = (kind: string, n: number) => `${kind}-${String(n).padStart(5, "0")}`;

export const LOCATION_KINDS = [["warehouse", "Warehouse"], ["shop", "Shop"], ["holding", "Holding area (not for sale)"]] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number][0];
export const LOCATION_KIND_LABEL: Record<string, string> = Object.fromEntries(LOCATION_KINDS);

export const DIRECTIONS = [["out", "Out only"], ["in", "In only"], ["both", "In or out"]] as const;
export type Direction = (typeof DIRECTIONS)[number][0];
export const DIRECTION_LABEL: Record<string, string> = Object.fromEntries(DIRECTIONS);

export const VALUE_METHODS = [["average", "Average landed cost"]] as const;

export interface InventorySettings {
  enabled: boolean;
  /** When it was first switched on (shipments received before then are not waiting to be received). */
  since: string | null;
  defaultLocationId: string | null;
  allowNegative: boolean;
  valueMethod: "average";
  /** Customs duty (%) for a product with no HS duty rate of its own, in each shipment's landed cost (null: none). */
  dutyPct: number | null;
  /** Update a GRN's cost when freight, duty or other invoices arrive after the goods were received. */
  autoRecost: boolean;
}
export const INVENTORY_DEFAULTS: InventorySettings = { enabled: false, since: null, defaultLocationId: null, allowNegative: false, valueMethod: "average", dutyPct: null, autoRecost: true };

export function normalizeInventorySettings(v: unknown): InventorySettings {
  const x = (v && typeof v === "object" ? v : {}) as any;
  return {
    enabled: x.enabled === true,
    since: typeof x.since === "string" && x.since ? x.since : null,
    defaultLocationId: typeof x.defaultLocationId === "string" && x.defaultLocationId ? x.defaultLocationId : null,
    allowNegative: x.allowNegative === true,
    valueMethod: "average",
    dutyPct: typeof x.dutyPct === "number" && Number.isFinite(x.dutyPct) && x.dutyPct >= 0 && x.dutyPct <= 100 ? x.dutyPct : null,
    autoRecost: x.autoRecost !== false,
  };
}

/** One received piece's landed cost, split up (home currency), and where each part came from. */
export interface CostDetail {
  fob: number; freight: number; duty: number; commission: number; other: number; total: number;
  /** "Actual" (paid), "Invoiced", "Estimated" or "" for each part. */
  sources: { freight: string; duty: string; commission: string; other: string };
  /** The customs duty rate used when the product had no HS rate (the Settings → Inventory default). */
  defaultDuty?: number | null;
  commissionPct?: number;
}
/** Freight is known and nothing is an estimate (paid or invoiced): the cost is final. */
export const costIsFinal = (c: CostDetail | null | undefined) => !!c && !!c.sources.freight && [c.sources.freight, c.sources.duty, c.sources.other].every((x) => x !== "Estimated");

export interface CostMove { key: string; kind: string; qty: number; unitCost: number | null }
/** Weighted average cost, move by move (in date order): each receipt with a cost mixes into the stock already held;
 *  stock going out leaves at the average and doesn't change it; transfers move stock without changing it. Returns the
 *  average after each move (null until there is a cost) and the final average per key. */
export function runningAverage(moves: CostMove[]): { after: Array<number | null>; avg: Map<string, number> } {
  const st = new Map<string, { q: number; avg: number | null }>();
  const after: Array<number | null> = [];
  for (const m of moves) {
    const s = st.get(m.key) || { q: 0, avg: null };
    if (m.kind !== "TRF") {
      if (m.qty > 0 && m.unitCost !== null) {
        const q = s.q + m.qty;
        s.avg = s.q > 0 && s.avg !== null ? (s.q * s.avg + m.qty * m.unitCost) / q : m.unitCost;
        s.q = q;
      } else s.q += m.qty;
    }
    st.set(m.key, s);
    after.push(s.avg === null ? null : Math.round(s.avg * 10000) / 10000);
  }
  const avg = new Map<string, number>();
  for (const [k, s] of st) if (s.avg !== null) avg.set(k, Math.round(s.avg * 10000) / 10000);
  return { after, avg };
}

/** The locations and reasons an organisation starts with when inventory is switched on (all can be changed). */
export const STARTER_LOCATIONS: Array<{ code: string; name: string; kind: LocationKind; sells: boolean; isDefault?: boolean }> = [
  { code: "MW", name: "Main Warehouse", kind: "warehouse", sells: true, isDefault: true },
];
export const STARTER_REASONS: Array<{ code: string; label: string; direction: Direction; onReceipt?: boolean; approveQty?: number }> = [
  { code: "DMG", label: "Damaged", direction: "out", onReceipt: true, approveQty: 50 },
  { code: "SHT", label: "Short in carton", direction: "out", onReceipt: true },
  { code: "OVR", label: "Extra in carton", direction: "in", onReceipt: true },
  { code: "CNT", label: "Stock count correction", direction: "both", approveQty: 100 },
  { code: "FND", label: "Found stock", direction: "in" },
  { code: "SMP", label: "Samples taken", direction: "out" },
  { code: "RTN", label: "Customer return", direction: "in" },
  { code: "SLS", label: "Sold outside Falcon", direction: "out" },
];

/** Codes: letters, digits and hyphens, up to 12. */
export const CODE_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,11}$/;

/** Can this reason be used for a change of this sign? */
export function reasonAllows(direction: string, change: number): boolean {
  if (!change) return true;
  if (direction === "in") return change > 0;
  if (direction === "out") return change < 0;
  return true;
}

/** Does an adjustment with this reason wait for an Admin? (above the reason's quantity or value, both as + or −) */
export function needsApproval(reason: { approveQty: number | null; approveValue: number | null }, totalQty: number, totalValue: number | null): boolean {
  const q = Math.abs(totalQty), v = totalValue === null ? null : Math.abs(totalValue);
  if (reason.approveQty !== null && reason.approveQty !== undefined && q > reason.approveQty) return true;
  if (reason.approveValue !== null && reason.approveValue !== undefined && v !== null && v > reason.approveValue) return true;
  return false;
}

/** Quantities: whole or two decimals (fabric in metres), never more than 9,999,999. */
export const round2 = (n: number) => Math.round(n * 100) / 100;
export const qtyOk = (n: number) => Number.isFinite(n) && Math.abs(n) <= 9_999_999 && round2(n) === n;

/** A stock key: one item × colour × size. */
export const stockKey = (itemId: string, colour: string, size: string) => `${itemId}\u0000${colour}\u0000${size}`;

/** The average landed cost of what was received (receipts and opening stock with a cost); null when none had a cost. */
export function averageCost(receipts: Array<{ qty: number; unitCost: number | null }>): number | null {
  let q = 0, v = 0;
  for (const r of receipts) if (r.unitCost !== null && r.unitCost !== undefined && r.qty > 0) { q += r.qty; v += r.qty * r.unitCost; }
  return q > 0 ? Math.round((v / q) * 10000) / 10000 : null;
}

/** The difference between received and shipped for one size, in words for the GRN ("−4 short", "+2 over"). */
export function differenceText(received: number, shipped: number): string {
  const d = round2(received - shipped);
  return d === 0 ? "0" : d < 0 ? `${d}` : `+${d}`;
}

/** Lines of a stock list, opening-stock import: "SKU, location code, qty, unit cost" or "code, colour, size, location, qty, unit cost". */
export interface ImportRow { line: number; sku?: string; code?: string; colour?: string; size?: string; location: string; qty: number; unitCost: number | null }
export function parseImport(text: string): { rows: ImportRow[]; errors: string[] } {
  const rows: ImportRow[] = [], errors: string[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((raw, i) => {
    const line = i + 1;
    if (!raw.trim()) return;
    const cells = (raw.includes("\t") ? raw.split("\t") : raw.split(/[,;]/)).map((c) => c.trim().replace(/^"|"$/g, ""));
    // A header row (no number where the quantity goes) is skipped.
    const num = (s: string | undefined) => (s === undefined || s === "" ? null : Number(s.replace(/[\s,](?=\d{3}\b)/g, "")));
    if (cells.length >= 5) {
      const qty = num(cells[4]);
      if (qty === null || !Number.isFinite(qty)) { if (i > 0) errors.push(`Line ${line}: the quantity "${cells[4] ?? ""}" isn't a number.`); return; }
      const cost = num(cells[5]);
      rows.push({ line, code: cells[0], colour: cells[1], size: cells[2], location: cells[3], qty, unitCost: cost !== null && Number.isFinite(cost) ? cost : null });
    } else if (cells.length >= 3) {
      const qty = num(cells[2]);
      if (qty === null || !Number.isFinite(qty)) { if (i > 0) errors.push(`Line ${line}: the quantity "${cells[2] ?? ""}" isn't a number.`); return; }
      const cost = num(cells[3]);
      rows.push({ line, sku: cells[0], location: cells[1], qty, unitCost: cost !== null && Number.isFinite(cost) ? cost : null });
    } else errors.push(`Line ${line}: expected SKU, location, quantity (and cost), or code, colour, size, location, quantity (and cost).`);
  });
  for (const r of rows) {
    if (!qtyOk(r.qty) || r.qty <= 0) errors.push(`Line ${r.line}: the quantity must be more than 0 (two decimals at most).`);
    if (r.unitCost !== null && (r.unitCost < 0 || r.unitCost > 1e9)) errors.push(`Line ${r.line}: the cost can't be below 0.`);
  }
  return { rows, errors };
}

/** Where stock stands against its levels: below the minimum, at it, above the maximum, or fine ("" when no levels). */
export type LevelStatus = "" | "below" | "atmin" | "over" | "ok";
export const LEVEL_STATUS_LABEL: Record<LevelStatus, string> = { "": "", below: "Below minimum", atmin: "At minimum", over: "Above maximum", ok: "OK" };
export function levelStatus(qty: number, min: number | null | undefined, max: number | null | undefined): LevelStatus {
  const hasMin = min !== null && min !== undefined, hasMax = max !== null && max !== undefined;
  if (!hasMin && !hasMax) return "";
  if (hasMin && qty < min! - 0.0001) return "below";
  if (hasMin && Math.abs(qty - min!) < 0.0001) return "atmin";
  if (hasMax && qty > max! + 0.0001) return "over";
  return "ok";
}

/** Splits one pasted / CSV line into cells (tab from Excel, otherwise comma or semicolon). */
const cellsOf = (raw: string) => (raw.includes("\t") ? raw.split("\t") : raw.split(/[,;]/)).map((c) => c.trim().replace(/^"|"$/g, ""));
const numOf = (s: string | undefined) => (s === undefined || s.trim() === "" ? null : Number(s.replace(/[\s,](?=\d{3}\b)/g, "")));

/** A pasted list of items: "code, quantity" (and for adjustments an optional reason code) on each line. The code is a
 *  SKU, a barcode, or a fabric / trim code. A first line without a number (a header) is skipped. */
export interface CodeListRow { line: number; code: string; qty: number; reason: string }
export function parseCodeList(text: string): { rows: CodeListRow[]; errors: string[] } {
  const rows: CodeListRow[] = [], errors: string[] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = i + 1;
    if (!raw.trim()) return;
    const c = cellsOf(raw);
    const q = numOf(c[1]);
    if (!c[0] || q === null || !Number.isFinite(q)) {
      if (i > 0 || (c[1] !== undefined && /\d/.test(c[1]))) errors.push(`Line ${line}: expected a code and a quantity, for example "CV-210-NV-M, 12".`);
      return;
    }
    if (!qtyOk(q)) { errors.push(`Line ${line}: use a whole number or two decimals.`); return; }
    rows.push({ line, code: c[0], qty: q, reason: (c[2] || "").toUpperCase() });
  });
  if (rows.length > 1000) errors.push("Paste up to 1,000 lines at a time.");
  return { rows, errors };
}

/** A stock levels list: "code, location code, minimum, maximum" on each line (blank = not set). */
export interface LevelRow { line: number; code: string; location: string; min: number | null; max: number | null }
export function parseLevels(text: string): { rows: LevelRow[]; errors: string[] } {
  const rows: LevelRow[] = [], errors: string[] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = i + 1;
    if (!raw.trim()) return;
    const c = cellsOf(raw);
    if (c.length < 3) { errors.push(`Line ${line}: expected code, location, minimum, maximum.`); return; }
    const min = numOf(c[2]), max = numOf(c[3]);
    const bad = (n: number | null) => n !== null && (!Number.isFinite(n) || n < 0 || !qtyOk(n));
    if (bad(min) || bad(max)) { if (i > 0) errors.push(`Line ${line}: the minimum and maximum must be numbers of 0 or more (or blank).`); return; }
    if (min !== null && max !== null && max < min) { errors.push(`Line ${line}: the maximum can't be below the minimum.`); return; }
    rows.push({ line, code: c[0], location: c[1], min, max });
  });
  if (rows.length > 20000) errors.push("Load up to 20,000 lines at a time.");
  return { rows, errors };
}
